import { Injectable } from '@angular/core';
import { initializeApp } from 'firebase/app';
import { FirebaseConfig, FirebaseService } from './firebase.service';
import {
  UserInfo,
  Auth,
  createUserWithEmailAndPassword,
  FacebookAuthProvider,
  getAuth,
  onAuthStateChanged,
  GoogleAuthProvider,
  OAuthProvider,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import { FirebaseAuthentication, User } from '@capacitor-firebase/authentication';

@Injectable({
  providedIn: 'root',
})
export class DesktopFirebaseService implements FirebaseService {
  private auth: Auth;

  constructor(configuration: FirebaseConfig) {
    // Initialize Firebase
    const app = initializeApp(configuration);
    // Initialize Firebase Authentication and get a reference to the service
    this.auth = getAuth(app);
  }

  async getUser(): Promise<User | null> {
    const res = await FirebaseAuthentication.getCurrentUser();
    return res.user;
  }


  /**
   * See FirebaseService.waitForRestoredUser.
   *
   * The web SDK answers this directly: onAuthStateChanged fires once the
   * session has been restored, whether or not there is a user.
   */
  async waitForRestoredUser(timeoutMs: number): Promise<User | null> {
    /*
     * ASKED OF THE SAME OBJECT THE TOKEN COMES FROM.
     *
     * This used to short-circuit on getUser(), which reads the Capacitor
     * plugin, while the token is read from `this.auth` and the subscription
     * below listens to `this.auth`. When those two disagreed, and at launch
     * they can, the wait returned « somebody is here » and getIdToken still
     * had nobody to ask. Waiting on one object and reading from another is
     * not waiting.
     */
    await this.auth.authStateReady();
    if (this.auth.currentUser) {
      return this.getUser();
    }

    return new Promise<User | null>((resolve) => {
      let settled = false;

      const finish = (user: User | null) => {
        if (settled) {
          return;
        }
        settled = true;
        clearTimeout(timer);
        unsubscribe();
        resolve(user);
      };

      const timer = setTimeout(() => finish(null), timeoutMs);
      const unsubscribe = onAuthStateChanged(this.auth, () => {
        void this.getUser().then((user) => {
          if (user) {
            finish(user);
          }
        });
      });
    });
  }

  /**
   * Sign in with email and password
   * @param email
   * @param password
   * @returns an access token
   */
  async signInWithEmailAndPassword(email: string, password: string): Promise<string> {
    const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
    return userCredential.user.getIdToken();
  }

  /**
   *
   * @param provider
   * @returns
   */
  async signInWithProvider(provider: string): Promise<string> {
    const authProvider = (() => {
      switch (provider) {
        case 'google':
          return new GoogleAuthProvider();
        case 'facebook':
          return new FacebookAuthProvider();
        case 'apple':
          return new OAuthProvider('apple.com');
        default:
          throw new Error(`Unsupported provider: ${provider}`);
      }
    })();
    const userCredential = await signInWithPopup(this.auth, authProvider);
    return userCredential.user.getIdToken();
  }

  async signUp(email: string, password: string): Promise<string> {
    const userCredential = await createUserWithEmailAndPassword(this.auth, email, password);
    return userCredential.user.getIdToken();
  }

  /**
   * CAPACITOR-V IS THIS LINE'S QUESTION MARK.
   *
   * `currentUser` is null until the web SDK has finished restoring the session,
   * and the optional chaining below turned that into `undefined` without a
   * word. The caller then threw « Firebase token not returned », which reads
   * like Firebase refusing a token when what really happened is that nobody
   * had asked it yet.
   *
   * The event that opened CAPACITOR-V says so plainly: the token came back
   * empty, and a moment later the very same report found a user present. Two
   * reads of the same session, a few milliseconds apart, disagreeing.
   *
   * authStateReady resolves as soon as that restore has finished, and
   * IMMEDIATELY once it already has, which is every call but the first. So the
   * ordinary path costs nothing and the first one stops lying.
   *
   * An `undefined` after this point means something: the web SDK has settled
   * and there is genuinely nobody signed in.
   */
  async getIdToken(forceRefresh = false): Promise<string | undefined> {
    await this.auth.authStateReady();
    return this.auth.currentUser?.getIdToken(forceRefresh);
  }

  async signOut(): Promise<void> {
    await signOut(this.auth);
  }

  async sendPasswordResetEmail(email: string): Promise<boolean> {
    await sendPasswordResetEmail(this.auth, email);
    return true;
  }

  async signUpProvider(
    provider: string,
  ): Promise<{ token: string; user: Pick<UserInfo, 'email' | 'displayName' | 'photoURL'> }> {
    const userCredential = await signInWithPopup(this.auth, new OAuthProvider(provider));
    return {
      token: await userCredential.user.getIdToken(),
      user: {
        email: userCredential.user.email,
        displayName: userCredential.user.displayName,
        photoURL: userCredential.user.photoURL,
      },
    };
  }
}
